import { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import moment from "moment";
import LendyIcon from "../components/lendy/LendyIcon";
import AssistantAvatar from "./AssistantAvatar";
import RowMenu from "./RowMenu";
import { useAgentNews } from "./agentNews";
import AskAddyV2, { defaultThreadId } from "../models/askAddyV2";
import { THREAD_UPDATED_EVENT } from "./runtime";
import { legacyChatThreadId, listLegacyChatsPage, mergeLegacyChats, renameLegacyChat } from "./legacyChats";
import "./v2ChatsDrawer.css";

const LAST_WEEK_DAYS = 7;
// v2 chats load this many at a time, the next page as the end of the list scrolls into view.
const THREADS_PAGE_SIZE = 20;
// The server refuses longer names: past this the drawer would cut them off.
const MAX_CHAT_TITLE_CHARS = 60;
const MAX_FOLDER_NAME_CHARS = 40;

/** Today, the last week, then by month: the drawer's sections. */
const getSection = (updatedAt) => {
  const at = moment(updatedAt);
  if (at.isSame(moment(), "day")) return "Today";
  if (at.isAfter(moment().subtract(LAST_WEEK_DAYS, "days"))) return "Last 7 days";
  return at.isSame(moment(), "year") ? at.format("MMMM") : at.format("MMMM YYYY");
};

/** "2 hours ago" today, "Sep 27" before. */
export const getWhen = (updatedAt) => {
  const at = moment(updatedAt);
  return at.isSame(moment(), "day") ? at.fromNow() : at.format("MMM D");
};

const byNewest = (first, second) => moment(second.updatedAt).valueOf() - moment(first.updatedAt).valueOf();
// The chat pinned last comes first.
const byPinnedLast = (first, second) => (second.metadata?.pinnedAt || 0) - (first.metadata?.pinnedAt || 0);

/** A classic chat as a drawer row: it opens as the v2 conversation that continues it here. */
const toLegacyRow = (identity) => ({ chatId, title, updatedAt, loanId, isBrowserOnly }) => ({
  id: legacyChatThreadId(identity, chatId),
  chatId,
  title,
  updatedAt,
  metadata: loanId ? { loanId } : {},
  isLegacy: true,
  isBrowserOnly
});

/**
 * What the drawer last listed, per user and per loan, scenario or general list, so reopening it shows
 * the chats at once while they reload (it unmounts whenever its panel closes). Kept for this page only.
 */
const listedChatsCache = new Map();

/** Tests: start every drawer from nothing. */
export const clearListedChatsCache = () => listedChatsCache.clear();

/** A reloaded first page with the older pages already listed: the reload wins for the chats in it. */
const mergeFirstPage = (listed, firstPage) => {
  const ids = new Set(firstPage.map(({ id }) => id));

  return [...firstPage, ...(listed || []).filter(({ id }) => !ids.has(id))];
};

/**
 * This loan's conversations (or the general ones off a loan) with the classic chats, the open
 * loan's borrowers. Reloaded when the drawer opens and after
 * every run. Both kinds come a page at a time: the next pages load when `olderChatsRef` (the end
 * of the list) comes into view.
 */
export const useDrawerData = ({ uid, organizationId, loanId, scenarioId, activeThreadId, isDrawerOpen }) => {
  const context = [uid, organizationId, loanId, scenarioId].join("|");
  const [threads, setThreads] = useState(() => listedChatsCache.get(`threads:${context}`)?.threads ?? null);
  const [threadsNextPage, setThreadsNextPage] = useState(() => listedChatsCache.get(`threads:${context}`)?.nextPage ?? null);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  // The loan or scenario the listed chats belong to: another one starts the list over.
  const listedContextRef = useRef("");
  const [legacyCursor, setLegacyCursor] = useState(null);
  const [legacy, setLegacy] = useState(() => ({ chats: [], nextCursor: null, ...listedChatsCache.get(`legacy:${uid}`), isLoading: false }));
  const olderChatsRef = useRef(null);
  const [borrowers, setBorrowers] = useState("");
  const [folders, setFolders] = useState(() => listedChatsCache.get(`folders:${uid}|${organizationId}`) ?? []);

  useEffect(() => {
    if (threads && listedContextRef.current) {
      listedChatsCache.set(`threads:${listedContextRef.current}`, { threads, nextPage: threadsNextPage });
    }
  }, [threads, threadsNextPage]);

  useEffect(() => {
    if (uid && !legacy.isLoading) {
      listedChatsCache.set(`legacy:${uid}`, { chats: legacy.chats, nextCursor: legacy.nextCursor });
    }
  }, [uid, legacy]);

  useEffect(() => {
    if (uid && organizationId) {
      listedChatsCache.set(`folders:${uid}|${organizationId}`, folders);
    }
  }, [uid, organizationId, folders]);

  useEffect(() => {
    if (!uid || !organizationId) return undefined;
    const identity = { organizationId, userId: uid };
    // Another loan or scenario shows what it listed last (or loading rows) until its chats arrive.
    if (listedContextRef.current && listedContextRef.current !== context) {
      setThreads(listedChatsCache.get(`threads:${context}`)?.threads ?? null);
      setThreadsNextPage(listedChatsCache.get(`threads:${context}`)?.nextPage ?? null);
    }
    let cancelled = false;
    const load = () =>
      AskAddyV2.threadsPage(loanId, identity, scenarioId, { page: 0, perPage: THREADS_PAGE_SIZE })
        .then(({ threads: firstPage, nextPage }) => {
          if (cancelled) {
            return;
          }
          const isSameContext = listedContextRef.current === context;
          listedContextRef.current = context;
          setThreads((listed) => (isSameContext ? mergeFirstPage(listed, firstPage) : firstPage));
          // A reload keeps the pages already scrolled to.
          if (!isSameContext) {
            setThreadsNextPage(nextPage);
          }
        })
        .catch(() => !cancelled && setThreads((listed) => listed || []));
    load();
    window.addEventListener(THREAD_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(THREAD_UPDATED_EVENT, load);
    };
  }, [uid, organizationId, loanId, scenarioId, activeThreadId, isDrawerOpen]); // eslint-disable-line -- context is made of these

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    setLegacy((current) => ({ ...current, isLoading: true }));
    listLegacyChatsPage(uid, legacyCursor)
      .then(({ chats, nextCursor }) => !cancelled && setLegacy((current) => ({ chats: mergeLegacyChats(current.chats, chats), nextCursor, isLoading: false })))
      // The drawer keeps the v2 chats and the classic ones already loaded.
      .catch(() => !cancelled && setLegacy((current) => ({ ...current, nextCursor: null, isLoading: false })));
    return () => {
      cancelled = true;
    };
  }, [uid, legacyCursor]);

  const loadMoreThreads = () => {
    setIsLoadingThreads(true);
    AskAddyV2.threadsPage(loanId, { organizationId, userId: uid }, scenarioId, { page: threadsNextPage, perPage: THREADS_PAGE_SIZE })
      .then(({ threads: page, nextPage }) => {
        setThreads((listed) => [...(listed || []), ...page.filter(({ id }) => !(listed || []).some((row) => row.id === id))]);
        setThreadsNextPage(nextPage);
      })
      // The chats already listed stay; scrolling back to the end doesn't retry a failing page forever.
      .catch(() => setThreadsNextPage(null))
      .finally(() => setIsLoadingThreads(false));
  };

  const { chats: legacyChats, nextCursor: legacyNextCursor, isLoading: isLoadingLegacy } = legacy;
  const isLoadingOlder = isLoadingLegacy || isLoadingThreads;
  const hasThreads = threads !== null;
  const hasMore = Boolean(legacyNextCursor) || threadsNextPage !== null;
  useEffect(() => {
    const endOfList = olderChatsRef.current;
    // Waits for the v2 chats, so an empty drawer doesn't pull a second page straight away.
    if (!endOfList || !hasMore || isLoadingOlder || !hasThreads) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        return;
      }
      if (threadsNextPage !== null) {
        loadMoreThreads();
      }
      if (legacyNextCursor) {
        setLegacyCursor(legacyNextCursor);
      }
    });
    observer.observe(endOfList);
    return () => observer.disconnect();
  }, [hasMore, legacyNextCursor, threadsNextPage, isLoadingOlder, hasThreads]); // eslint-disable-line -- loadMoreThreads reads the same state

  useEffect(() => {
    if (!uid || !organizationId || !isDrawerOpen) return undefined;
    const identity = { organizationId, userId: uid };
    let cancelled = false;
    // The borrowers are an extra on the drawer: without them it still lists the chats.
    if (loanId) {
      AskAddyV2.loanSummary(loanId, identity)
        .then((summary) => !cancelled && setBorrowers((summary?.borrowers || []).join(", ")))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [uid, organizationId, loanId, isDrawerOpen]);

  useEffect(() => {
    if (!uid || !organizationId || !isDrawerOpen) return undefined;
    let cancelled = false;
    // Without its folders the drawer still lists every chat, by date.
    AskAddyV2.chatFolders
      .list({ organizationId, userId: uid })
      .then((rows) => !cancelled && setFolders(rows || []))
      .catch(() => !cancelled && setFolders([]));
    return () => {
      cancelled = true;
    };
  }, [uid, organizationId, isDrawerOpen]);

  const removeThread = (threadId) => setThreads((rows) => (rows || []).filter(({ id }) => id !== threadId));

  const setThreadMetadata = (threadId, metadata) => setThreads((rows) => (rows || []).map((row) => (row.id === threadId ? { ...row, metadata } : row)));

  // A classic chat pinned or filed is stored as its v2 conversation, which then lists in its place.
  const adoptLegacyChat = ({ id, title, updatedAt }, metadata) =>
    setThreads((rows) => [...(rows || []).filter((row) => row.id !== id), { id, title, updatedAt, metadata }]);

  // A deleted folder's chats go back to the list.
  const removeFolder = (folderId) => {
    setFolders((rows) => rows.filter(({ id }) => id !== folderId));
    setThreads((rows) =>
      (rows || []).map((row) => {
        if (row.metadata?.folderId !== folderId) {
          return row;
        }
        const { folderId: _filed, ...metadata } = row.metadata;

        return { ...row, metadata };
      })
    );
  };

  const renameRow = ({ id, chatId, isLegacy }, title) => {
    if (isLegacy) {
      setLegacy((current) => ({ ...current, chats: current.chats.map((chat) => (chat.chatId === chatId ? { ...chat, title } : chat)) }));

      return;
    }
    setThreads((rows) => (rows || []).map((row) => (row.id === id ? { ...row, title } : row)));
  };

  const threadIds = new Set((threads || []).map(({ id }) => id));
  // A classic chat already continued here is listed once, as its v2 conversation.
  const legacyRows = legacyChats.map(toLegacyRow({ organizationId, userId: uid, loanId, scenarioId })).filter(({ id }) => !threadIds.has(id));
  const chats = threads && [...threads, ...legacyRows].sort(byNewest);

  return { chats, borrowers, removeThread, renameRow, isLoadingOlder, olderChatsRef, folders, setFolders, setThreadMetadata, adoptLegacyChat, removeFolder };
};

// The board's tooltip: dark in both colour modes.
const INFO_TOOLTIP_STYLE = { bgcolor: "#1f2433", borderRadius: "8px", px: 1.25, py: 1, fontSize: 12.5, lineHeight: 1.45 };
// Long enough that moving down the list doesn't flash a tooltip on every row.
const INFO_TOOLTIP_DELAY_MS = 600;
const DRAGGED_CHAT_TYPE = "application/x-addy-chat-id";
const COLLAPSED_SECTIONS_KEY = "aav2-drawer-collapsed-sections";

const readCollapsedSections = () => {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(COLLAPSED_SECTIONS_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

const saveCollapsedSections = (sections) => {
  try {
    window.localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify([...sections]));
  } catch {
    // Private windows and blocked storage: the sections still open and close, just not remembered.
  }
};

/** Something a dragged chat can be dropped on: highlighted while one is over it. */
const useChatDrop = (onDropChat) => {
  const [isOver, setIsOver] = useState(false);
  // Moving onto the target's own text leaves one element and enters another: count them, so the
  // highlight only ends when the pointer leaves the whole target (Safari's dragleave has no relatedTarget).
  const enteredRef = useRef(0);
  if (!onDropChat) {
    return { isOver: false, dropProps: {} };
  }

  const hasChat = (event) => Array.from(event.dataTransfer?.types || []).includes(DRAGGED_CHAT_TYPE);

  return {
    isOver,
    dropProps: {
      onDragEnter: (event) => {
        if (!hasChat(event)) {
          return;
        }
        enteredRef.current += 1;
        setIsOver(true);
      },
      onDragOver: (event) => {
        if (!hasChat(event)) {
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        setIsOver(true);
      },
      onDragLeave: () => {
        enteredRef.current = Math.max(enteredRef.current - 1, 0);
        if (!enteredRef.current) {
          setIsOver(false);
        }
      },
      onDrop: (event) => {
        enteredRef.current = 0;
        setIsOver(false);
        const chatId = event.dataTransfer.getData(DRAGGED_CHAT_TYPE);
        if (!chatId) {
          return;
        }
        event.preventDefault();
        onDropChat(chatId);
      },
    },
  };
};

/** Deleting a chat (or a folder) asks first, in its own row. */
const DeleteChatConfirm = ({ chatTitle, onCancel, onDelete, question = "Delete this chat?", detail, errorText = "Couldn't delete this chat. Try again." }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");

  const remove = async () => {
    setIsDeleting(true);
    setError("");
    try {
      await onDelete();
    } catch {
      setError(errorText);
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="aav2-drawer-chat aav2-drawer-chat--confirm"
      role="group"
      aria-label={`Delete ${chatTitle}`}
      onKeyDown={(event) => event.key === "Escape" && !isDeleting && onCancel()}
    >
      <span className="aav2-drawer-confirm-text">
        <b>{question}</b>
        <small className={error ? "is-error" : undefined} role={error ? "alert" : undefined}>
          {error || detail || chatTitle}
        </small>
      </span>
      <span className="aav2-drawer-confirm-actions">
        <button type="button" className="aav2-drawer-confirm-btn" onClick={onCancel} disabled={isDeleting} autoFocus>
          Cancel
        </button>
        <button type="button" className="aav2-drawer-confirm-btn aav2-drawer-confirm-btn--danger" onClick={remove} disabled={isDeleting}>
          {isDeleting ? "Deleting…" : "Delete"}
        </button>
      </span>
    </div>
  );
};

/** A chat's icon: the loan's for a loan chat. Used by the drawer's rows and the chat search. */
export const ChatTile = ({ isLoanChat }) => (
  <span className={`aav2-drawer-tile ${isLoanChat ? "aav2-drawer-tile--loan" : ""}`.trim()}>
    <LendyIcon name={isLoanChat ? "home" : "chat"} size={15} />
  </span>
);

/**
 * Renaming a chat edits its title in the chat's own row: Enter or leaving the field saves, Escape cancels.
 * Folders are named the same way, with their own icon, label and limit.
 */
const RenameChatInput = ({
  chatTitle,
  onCancel,
  onRename,
  icon,
  fieldLabel = "Chat name",
  groupLabel = `Rename ${chatTitle}`,
  maxLength = MAX_CHAT_TITLE_CHARS,
  errorText = "Couldn't rename this chat. Try again.",
}) => {
  const [value, setValue] = useState(chatTitle);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const isCancelledRef = useRef(false);

  const save = async () => {
    const title = value.replace(/\s+/g, " ").trim();
    if (isCancelledRef.current || isSaving) {
      return;
    }
    if (!title || title === chatTitle) {
      onCancel();

      return;
    }
    setIsSaving(true);
    setError("");
    try {
      await onRename(title);
    } catch {
      setError(errorText);
      setIsSaving(false);
    }
  };

  const cancel = (event) => {
    if (event.key !== "Escape") {
      return;
    }
    isCancelledRef.current = true;
    onCancel();
  };

  return (
    <div className="aav2-drawer-chat aav2-drawer-chat--rename" role="group" aria-label={groupLabel} onKeyDown={cancel}>
      {icon && (
        <span className="aav2-drawer-row-icon" aria-hidden="true">
          <LendyIcon name={icon} size={16} />
        </span>
      )}
      <span className="aav2-drawer-rename">
        <input
          className="aav2-drawer-rename-input"
          aria-label={fieldLabel}
          value={value}
          maxLength={maxLength}
          readOnly={isSaving}
          autoFocus
          onFocus={(event) => event.target.select()}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
          onBlur={save}
        />
        {error && (
          <small className="is-error" role="alert">
            {error}
          </small>
        )}
      </span>
    </div>
  );
};

/**
 * A chat's ⋯ menu, like ChatGPT's: rename, pin, move to a folder (a new one, or out of its own), delete.
 * Classic chats have no Delete: the classic chat history keeps them.
 */
const getChatMenuItems = ({ thread, folders, onRename, onTogglePin, onMove, onNewFolder, onDelete }) => {
  const { pinned, folderId } = thread.metadata || {};
  const currentFolder = folders.find(({ id }) => id === folderId);

  return [
    { id: "rename", icon: "pen", label: "Rename", onSelect: onRename },
    { id: "pin", icon: "pin", label: pinned ? "Unpin" : "Pin", onSelect: onTogglePin },
    {
      id: "move",
      icon: "folder",
      label: "Move to folder",
      items: [
        ...folders
          .filter(({ id }) => id !== folderId)
          .map((folder) => ({ id: `move-${folder.id}`, icon: "folder", label: folder.name, onSelect: () => onMove(folder.id) })),
        { id: "new-folder", icon: "plus", label: "New folder", onSelect: onNewFolder },
        ...(currentFolder ? [{ id: "unfile", icon: "x", label: `Remove from ${currentFolder.name}`, onSelect: () => onMove(null) }] : []),
      ],
    },
    ...(onDelete ? [{ id: "delete", icon: "trash", label: "Delete", isDanger: true, onSelect: onDelete }] : []),
  ];
};

const ChatRow = ({ thread, isLoanChat, borrowers, isActive, onClick, onDelete, onRename, folders, onTogglePin, onMove, onCreateFolder }) => {
  const [mode, setMode] = useState("view");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [error, setError] = useState("");
  const { title, updatedAt } = thread;
  const chatTitle = title || "New chat";
  const lastActive = `Last active: ${moment(updatedAt).format("MMM D, YYYY [at] h:mm A")}`;
  const detail = isLoanChat ? ["Loan chat", borrowers || getWhen(updatedAt)].join(" · ") : getWhen(updatedAt);
  if (mode === "confirm-delete") {
    return <DeleteChatConfirm chatTitle={chatTitle} onCancel={() => setMode("view")} onDelete={onDelete} />;
  }
  if (mode === "rename") {
    const rename = async (newTitle) => {
      await onRename(newTitle);
      setMode("view");
    };

    return <RenameChatInput chatTitle={chatTitle} onCancel={() => setMode("view")} onRename={rename} />;
  }
  if (mode === "new-folder") {
    const createAndMove = async (name) => {
      await onCreateFolder(name);
      setMode("view");
    };

    return (
      <RenameChatInput
        chatTitle=""
        icon="folder"
        groupLabel={`New folder for ${chatTitle}`}
        fieldLabel="Folder name"
        maxLength={MAX_FOLDER_NAME_CHARS}
        errorText="Couldn't create this folder. Try again."
        onCancel={() => setMode("view")}
        onRename={createAndMove}
      />
    );
  }

  const runAction = (action, failure) => async () => {
    setError("");
    try {
      await action();
    } catch {
      setError(failure);
    }
  };
  const menuItems = getChatMenuItems({
    thread,
    folders,
    onRename: () => setMode("rename"),
    onTogglePin: runAction(onTogglePin, thread.metadata?.pinned ? "Couldn't unpin this chat. Try again." : "Couldn't pin this chat. Try again."),
    onMove: (folderId) => runAction(() => onMove(folderId), "Couldn't move this chat. Try again.")(),
    onNewFolder: () => setMode("new-folder"),
    onDelete: onDelete && (() => setMode("confirm-delete")),
  });
  // Any chat can be dragged onto a folder, Pinned or Chats.
  const dragProps = {
    draggable: true,
    onDragStart: (event) => {
      event.dataTransfer.setData(DRAGGED_CHAT_TYPE, thread.id);
      event.dataTransfer.effectAllowed = "move";
    },
  };

  return (
    <div className={`aav2-drawer-chat${isMenuOpen ? " is-menu-open" : ""}`} aria-current={isActive ? "true" : undefined} {...dragProps}>
      <Tooltip
        placement="bottom-start"
        enterDelay={INFO_TOOLTIP_DELAY_MS}
        enterNextDelay={INFO_TOOLTIP_DELAY_MS}
        componentsProps={{ tooltip: { sx: INFO_TOOLTIP_STYLE } }}
        title={
          <span className="aav2-drawer-info">
            <b>{chatTitle}</b>
            {isLoanChat && <span>{borrowers ? `Loan chat · Borrower(s): ${borrowers}` : "Loan chat"}</span>}
            <span>{lastActive}</span>
          </span>
        }
      >
        <button type="button" className="aav2-drawer-chat-open aav2-drawer-chat-open--detailed" onClick={onClick}>
          <ChatTile isLoanChat={isLoanChat} />
          <span className="aav2-drawer-chat-text">
            <b>{chatTitle}</b>
            <small>{detail}</small>
          </span>
        </button>
      </Tooltip>
      {error && (
        <small className="aav2-drawer-row-error" role="alert">
          {error}
        </small>
      )}
      <span className="aav2-drawer-chat-actions">
        <RowMenu label={chatTitle} items={menuItems} onOpenChange={setIsMenuOpen} />
      </span>
    </div>
  );
};

/** A folder in the chats drawer: opens to show its chats, takes chats dropped on it, and can be renamed or deleted (its chats go back to the list). */
const FolderRow = ({ folder, chatCount, isOpen, onToggle, onRename, onDelete, onDropChat, children }) => {
  const [mode, setMode] = useState("view");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isOver, dropProps } = useChatDrop(onDropChat);

  const renderRow = () => {
    if (mode === "confirm-delete") {
      return (
        <DeleteChatConfirm
          chatTitle={folder.name}
          question="Delete this folder?"
          detail="Its chats move back to the list."
          errorText="Couldn't delete this folder. Try again."
          onCancel={() => setMode("view")}
          onDelete={onDelete}
        />
      );
    }
    if (mode === "rename") {
      const rename = async (name) => {
        await onRename(name);
        setMode("view");
      };

      return (
        <RenameChatInput
          chatTitle={folder.name}
          icon="folder"
          groupLabel={`Rename folder ${folder.name}`}
          fieldLabel="Folder name"
          maxLength={MAX_FOLDER_NAME_CHARS}
          errorText="Couldn't rename this folder. Try again."
          onCancel={() => setMode("view")}
          onRename={rename}
        />
      );
    }

    return (
      <div className={`aav2-drawer-chat${isMenuOpen ? " is-menu-open" : ""}${isOver ? " is-drop-target" : ""}`} {...dropProps}>
        <button type="button" className="aav2-drawer-chat-open" aria-expanded={isOpen} onClick={onToggle}>
          <span className="aav2-drawer-row-icon" aria-hidden="true">
            <LendyIcon name="folder" size={16} />
          </span>
          <span className="aav2-drawer-chat-title">{folder.name}</span>
          {chatCount > 0 && <span className="aav2-drawer-row-count">{chatCount}</span>}
        </button>
        <span className="aav2-drawer-chat-actions">
          <RowMenu
            label={folder.name}
            onOpenChange={setIsMenuOpen}
            items={[
              { id: "rename", icon: "pen", label: "Rename", onSelect: () => setMode("rename") },
              { id: "delete", icon: "trash", label: "Delete", isDanger: true, onSelect: () => setMode("confirm-delete") },
            ]}
          />
        </span>
      </div>
    );
  };

  return (
    <div className="aav2-drawer-folder">
      {renderRow()}
      {isOpen && (
        <div className="aav2-drawer-folder-chats">
          {chatCount ? children : <p className="aav2-drawer-empty">No chats in this folder.</p>}
        </div>
      )}
    </div>
  );
};

const LOADING_ROW_WIDTHS = ["82%", "64%", "74%", "56%", "70%", "60%", "78%", "52%"];

/** Placeholder rows, one line each like the chats, while the list (or its next page) loads. */
const ChatRowsLoading = ({ count, label }) => (
  <div className="aav2-drawer-loading" role="status" aria-label={label}>
    {LOADING_ROW_WIDTHS.slice(0, count).map((width) => (
      <span key={width} className="aav2-drawer-loading-row" aria-hidden="true">
        <span className="aav2-drawer-loading-bar" style={{ width }} />
      </span>
    ))}
  </div>
);

/** Pinned, Folders and Chats: each opens and closes from its title, and can take a dragged chat. */
const DrawerSection = ({ title, isCollapsed, onToggle, onDropChat, action, children }) => {
  const { isOver, dropProps } = useChatDrop(onDropChat);

  return (
    <div className={`aav2-drawer-section${isOver ? " is-drop-target" : ""}`} {...dropProps}>
      <div className="aav2-drawer-section-head">
        <button type="button" className="aav2-drawer-section-toggle" aria-expanded={!isCollapsed} onClick={onToggle}>
          {title}
          <span className={`aav2-drawer-section-chevron${isCollapsed ? "" : " is-open"}`} aria-hidden="true">
            <LendyIcon name="right" size={12} />
          </span>
        </button>
        {action}
      </div>
      {!isCollapsed && children}
    </div>
  );
};

/**
 * Ask Addy v2's chats drawer (board 7 of the Lendy sidebar design): New chat, Apps & MCP,
 * a search, and the conversations: Pinned, Folders, then the rest newest first. Classic chats
 * are listed with them; they can be renamed but not deleted from here, since the classic chat
 * history has no delete.
 */
const V2ChatsDrawer = ({
  uid,
  organizationId,
  loanId,
  scenarioId,
  activeThreadId,
  isDrawerOpen,
  isSearchRequested,
  onSearchShown,
  onThreadClick,
  onNewChat,
  onOpenApps,
  clientChat,
  onOpenClientChat,
  onCollapse,
  assistant,
  onOpenProfile,
}) => {
  const [query, setQuery] = useState("");
  const [openFolderIds, setOpenFolderIds] = useState(() => new Set());
  const [collapsedSections, setCollapsedSections] = useState(readCollapsedSections);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const searchRef = useRef(null);
  const { chats, borrowers, removeThread, renameRow, isLoadingOlder, olderChatsRef, folders, setFolders, setThreadMetadata, adoptLegacyChat, removeFolder } =
    useDrawerData({ uid, organizationId, loanId, scenarioId, activeThreadId, isDrawerOpen });
  const identity = { organizationId, userId: uid };

  useEffect(() => {
    if (!isSearchRequested) {
      return;
    }

    searchRef.current?.focus();
    onSearchShown?.();
  }, [isSearchRequested, onSearchShown]);

  const defaultId = defaultThreadId({ organizationId, userId: uid, loanId, scenarioId });
  const activeId = activeThreadId || defaultId;
  const needle = query.trim().toLowerCase();
  const shown = (chats || []).filter(({ title }) => !needle || (title || "New chat").toLowerCase().includes(needle));
  const folderIds = new Set(folders.map(({ id }) => id));
  // A search lists every match by date; otherwise pinned chats come first, then the folders, then the rest by date.
  const isOrganized = !needle;
  const pinnedChats = isOrganized ? shown.filter(({ metadata }) => metadata?.pinned).sort(byPinnedLast) : [];
  const isInFolder = ({ metadata }) => isOrganized && !metadata?.pinned && folderIds.has(metadata?.folderId);
  const datedChats = shown.filter((thread) => !thread.metadata?.pinned || !isOrganized).filter((thread) => !isInFolder(thread));
  const dates = [...new Set(datedChats.map(({ updatedAt }) => getSection(updatedAt)))];

  const toggleSection = (section) =>
    setCollapsedSections((current) => {
      const next = new Set(current);
      if (!next.delete(section)) {
        next.add(section);
      }
      saveCollapsedSections(next);

      return next;
    });

  // Deleting the open chat starts a new one, so the chat view never shows a deleted conversation.
  const deleteChat = async (threadId) => {
    await AskAddyV2.deleteThread(threadId, { organizationId, userId: uid });
    removeThread(threadId);
    if (threadId === activeId) {
      onNewChat();
    }
  };

  const renameChat = async (thread, title) => {
    if (thread.isLegacy) {
      await renameLegacyChat(uid, thread, title);
      renameRow(thread, title);

      return;
    }
    renameRow(thread, await AskAddyV2.renameThread(thread.id, title, { organizationId, userId: uid }));
  };

  const updateChat = async (thread, changes) => {
    if (!thread.isLegacy) {
      setThreadMetadata(thread.id, await AskAddyV2.updateThread(thread.id, changes, identity));

      return;
    }

    // Its first pin or move stores the classic chat as a v2 conversation, with its name and date.
    const title = (thread.title || "Chat").replace(/\s+/g, " ").trim().slice(0, MAX_CHAT_TITLE_CHARS);
    const metadata = await AskAddyV2.updateThread(thread.id, { ...changes, title, lastActiveAt: moment(thread.updatedAt).valueOf() }, identity);
    adoptLegacyChat({ ...thread, title }, metadata);
  };

  // A drop has no row to show an error in: the chat simply stays where it was.
  const dropChat = (changes) => (threadId) => {
    const thread = (chats || []).find(({ id }) => id === threadId);
    if (!thread) {
      return;
    }

    updateChat(thread, changes).catch(() => undefined);
  };

  const toggleFolder = (folderId) =>
    setOpenFolderIds((current) => {
      const next = new Set(current);
      if (!next.delete(folderId)) {
        next.add(folderId);
      }

      return next;
    });

  const createFolder = async (name) => {
    const folder = await AskAddyV2.chatFolders.create(name, identity);
    setFolders((rows) => [...rows, folder]);

    return folder;
  };

  // A chat moved into a new folder opens it, so the chat stays in sight.
  const moveToNewFolder = async (thread, name) => {
    const folder = await createFolder(name);
    await updateChat(thread, { folderId: folder.id });
    setOpenFolderIds((current) => new Set(current).add(folder.id));
  };

  const renameFolder = async (folder, name) => {
    const renamed = await AskAddyV2.chatFolders.rename(folder.id, name, identity);
    setFolders((rows) => rows.map((row) => (row.id === folder.id ? renamed : row)));
  };

  const deleteFolder = async (folder) => {
    await AskAddyV2.chatFolders.remove(folder.id, identity);
    removeFolder(folder.id);
  };

  const renderChat = (thread) => (
    <ChatRow
      key={thread.id}
      thread={thread}
      isLoanChat={Boolean(thread.metadata?.loanId || (!thread.isLegacy && loanId))}
      // A classic chat may be about another loan than the open one.
      borrowers={!thread.isLegacy || thread.metadata?.loanId === loanId ? borrowers : ""}
      isActive={thread.id === activeId}
      onClick={() => onThreadClick(thread.id === defaultId ? null : thread.id)}
      onDelete={thread.isLegacy ? undefined : () => deleteChat(thread.id)}
      onRename={(title) => renameChat(thread, title)}
      folders={folders}
      onTogglePin={() => updateChat(thread, { pinned: !thread.metadata?.pinned })}
      onMove={(folderId) => updateChat(thread, { folderId })}
      onCreateFolder={(name) => moveToNewFolder(thread, name)}
    />
  );

  const renderDatedChats = () =>
    dates.map((date) => (
      <div key={date}>
        <div className="aav2-drawer-section-title">{date}</div>
        {datedChats.filter(({ updatedAt }) => getSection(updatedAt) === date).map(renderChat)}
      </div>
    ));

  return (
    <div className="aav2-drawer">
      {onCollapse && (
        <div className="aav2-drawer-top">
          <PanelToggle isExpanded onClick={onCollapse} />
        </div>
      )}
      <div className="aav2-drawer-body">
        <button type="button" className="aav2-drawer-new" onClick={onNewChat}>
          <LendyIcon name="plus" />
          New chat
        </button>
        {onOpenApps && (
          <button type="button" className="aav2-drawer-link" onClick={onOpenApps}>
            <LendyIcon name="apps" />
            <span className="aav2-drawer-link-label">Apps &amp; MCP</span>
            <LendyIcon name="right" size={14} />
          </button>
        )}
        {clientChat?.isAvailable && (
          <button type="button" className="aav2-drawer-link" onClick={onOpenClientChat}>
            <LendyIcon name="users" />
            <span className="aav2-drawer-link-label">{clientChat.label}</span>
            {clientChat.unreadCount > 0 && (
              <span className="aav2-drawer-count aav2-drawer-count--unread" title={`${clientChat.unreadCount} unread`}>
                {clientChat.unreadCount}
              </span>
            )}
            <LendyIcon name="right" size={14} />
          </button>
        )}
        <label className="aav2-drawer-search">
          <LendyIcon name="search" size={15} />
          <input
            ref={searchRef}
            placeholder="Search chats"
            aria-label="Search chats"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        {!chats && <ChatRowsLoading count={8} label="Loading chats" />}
        {chats && !chats.length && !isLoadingOlder && <p className="aav2-drawer-empty">No chats yet.</p>}
        {chats && chats.length > 0 && !shown.length && !isLoadingOlder && <p className="aav2-drawer-empty">No chats match.</p>}
        {pinnedChats.length > 0 && (
          <DrawerSection title="Pinned" isCollapsed={collapsedSections.has("pinned")} onToggle={() => toggleSection("pinned")} onDropChat={dropChat({ pinned: true })}>
            {pinnedChats.map(renderChat)}
          </DrawerSection>
        )}
        {isOrganized && chats && (
          <DrawerSection
            title="Folders"
            isCollapsed={collapsedSections.has("folders")}
            onToggle={() => toggleSection("folders")}
            action={
              <Tooltip title="New folder" arrow>
                <button type="button" className="aav2-row-menu-trigger" aria-label="New folder" onClick={() => setIsCreatingFolder(true)}>
                  <LendyIcon name="plus" size={14} />
                </button>
              </Tooltip>
            }
          >
            {isCreatingFolder && (
              <RenameChatInput
                chatTitle=""
                icon="folder"
                groupLabel="New folder"
                fieldLabel="Folder name"
                maxLength={MAX_FOLDER_NAME_CHARS}
                errorText="Couldn't create this folder. Try again."
                onCancel={() => setIsCreatingFolder(false)}
                onRename={async (name) => {
                  await createFolder(name);
                  setIsCreatingFolder(false);
                }}
              />
            )}
            {!folders.length && !isCreatingFolder && <p className="aav2-drawer-empty">No folders yet. Add one with +, then drag chats into it.</p>}
            {folders.map((folder) => {
              const folderChats = shown.filter((thread) => isInFolder(thread) && thread.metadata.folderId === folder.id);

              return (
                <FolderRow
                  key={folder.id}
                  folder={folder}
                  chatCount={folderChats.length}
                  isOpen={openFolderIds.has(folder.id)}
                  onToggle={() => toggleFolder(folder.id)}
                  onRename={(name) => renameFolder(folder, name)}
                  onDelete={() => deleteFolder(folder)}
                  onDropChat={dropChat({ folderId: folder.id })}
                >
                  {folderChats.map(renderChat)}
                </FolderRow>
              );
            })}
          </DrawerSection>
        )}
        {dates.length > 0 &&
          (isOrganized ? (
            <DrawerSection
              title="Chats"
              isCollapsed={collapsedSections.has("chats")}
              onToggle={() => toggleSection("chats")}
              onDropChat={dropChat({ pinned: false, folderId: null })}
            >
              {renderDatedChats()}
            </DrawerSection>
          ) : (
            renderDatedChats()
          ))}
        {isLoadingOlder && chats?.length > 0 && <ChatRowsLoading count={3} label="Loading older chats" />}
        <div ref={olderChatsRef} className="aav2-drawer-end" aria-hidden="true" />
      </div>
      {onOpenProfile && (
        <div className="aav2-drawer-foot">
          <button type="button" className="aav2-drawer-profile" onClick={onOpenProfile}>
            <AssistantAvatar imageUrl={assistant?.imageUrl} size="row" />
            <span className="aav2-drawer-chat-text">
              <b>{assistant?.name || "Assistant"}</b>
              <small>Identity and scheduled tasks</small>
            </span>
            <LendyIcon name="right" size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

/** Collapses the chats list on a narrow sidebar, where there is no rail to close it from. */
const PanelToggle = ({ isExpanded, onClick }) => {
  const label = isExpanded ? "Collapse chats" : "Expand chats";

  return (
    <Tooltip title={label} placement="right" arrow>
      <button type="button" className="aav2-rail-button" aria-label={label} aria-expanded={isExpanded} onClick={onClick}>
        <LendyIcon name="menu" size={20} />
      </button>
    </Tooltip>
  );
};

const RailButton = ({ icon, label, count, isActive, onClick }) => (
  <Tooltip title={count ? `${label} · ${count} unread` : label} placement="right" arrow>
    <button
      type="button"
      className={`aav2-rail-button${isActive ? " is-active" : ""}`}
      aria-label={count ? `${label}, ${count} unread` : label}
      aria-pressed={isActive}
      onClick={onClick}
    >
      <LendyIcon name={icon} size={20} />
      {count > 0 && <span className="aav2-rail-badge">{count}</span>}
    </button>
  </Tooltip>
);

/**
 * The always-on rail of a wide sidebar: the panel toggle on top (it reopens the last panel), the
 * chats and Apps & MCP as icons, and the assistant at the foot, where people look for
 * settings. Each icon opens its panel beside the rail and a second click closes it.
 */
export const V2ChatsRail = ({
  activePanel,
  onTogglePanel,
  onToggleChats,
  onNewChat,
  onSearch,
  isAppsOpen,
  onOpenApps,
  assistant,
  assistantDot,
  onOpenProfile,
  clientChat,
  onOpenClientChat,
}) => {
  const { hasNews } = useAgentNews();
  const profileLabel = `${assistant?.name || "Assistant"}: identity and scheduled tasks${hasNews ? " · something new" : ""}`;
  const toggleLabel = activePanel ? "Close panel" : "Open panel";

  return (
    <nav className="aav2-rail" aria-label="Ask Addy">
      <div className="aav2-rail-items">
        <Tooltip title={toggleLabel} placement="right" arrow>
          <button type="button" className="aav2-rail-button" aria-label={toggleLabel} aria-expanded={Boolean(activePanel)} onClick={onTogglePanel}>
            <LendyIcon name={activePanel ? "panelLeftClose" : "panelLeftOpen"} size={20} />
          </button>
        </Tooltip>
      </div>
      <div className="aav2-rail-items aav2-rail-items--group">
        <RailButton icon="chat" label="Chats" isActive={activePanel === "chats"} onClick={onToggleChats} />
        <RailButton icon="newChat" label="New chat" onClick={onNewChat} />
        <RailButton icon="search" label="Search chats" onClick={onSearch} />
        <RailButton icon="apps" label="Apps & MCP" isActive={isAppsOpen} onClick={onOpenApps} />
        {clientChat?.isAvailable && (
          <RailButton icon="users" label={clientChat.label} count={clientChat.unreadCount} onClick={onOpenClientChat} />
        )}
      </div>
      <Tooltip title={profileLabel} placement="right" arrow>
        <button
          type="button"
          className={`aav2-rail-profile${activePanel === "profile" ? " is-active" : ""}`}
          aria-label={profileLabel}
          aria-pressed={activePanel === "profile"}
          onClick={onOpenProfile}
        >
          <AssistantAvatar imageUrl={assistant?.imageUrl} size="row" dot={assistantDot} />
          {hasNews && (
            <span className="aav2-rail-news lendy-ping" aria-hidden="true">
              <i />
            </span>
          )}
        </button>
      </Tooltip>
    </nav>
  );
};

export default V2ChatsDrawer;
